/**
 * Map measurement tools: distance, area, coordinate search.
 * UX: crosshair coordinate search, live measurement tooltip, coords panel.
 */
(function() {
  'use strict';

  const maplibregl = window.maplibregl;
  const turf = window.turf;

  const SOURCE_ID = 'measure-source';
  const LINE_LAYER = 'measure-line';
  const FILL_LAYER = 'measure-fill';
  const POINTS_LAYER = 'measure-points';
  const PREVIEW_LAYER = 'measure-preview';

  let activeTool = null;      // 'length' | 'area' | 'search'
  let points = [];            // [[lng,lat], ...]
  let cursorLngLat = null;
  let popup = null;
  let measureClickHandler = null;
  let measureDblClickHandler = null;
  let measureMoveHandler = null;
  let keyHandler = null;
  let moveEndHandler = null;
  let searchClickHandler = null;

  function getMap() { return window.map; }
  function getTurf() { return window.turf; }

  function formatDistance(km) {
    if (km < 1) return (km * 1000).toFixed(1) + ' м';
    return km.toFixed(2) + ' км';
  }

  function formatArea(m2) {
    if (m2 >= 1000000) return (m2 / 1000000).toFixed(2) + ' км²';
    if (m2 >= 10000) return (m2 / 10000).toFixed(2) + ' га';
    return m2.toFixed(1) + ' м²';
  }

  function emptyFC() {
    return { type: 'FeatureCollection', features: [] };
  }

  function ensureSource(map) {
    if (map.getSource(SOURCE_ID)) return;
    map.addSource(SOURCE_ID, { type: 'geojson', data: emptyFC() });

    map.addLayer({
      id: PREVIEW_LAYER, type: 'line', source: SOURCE_ID,
      filter: ['==', ['get', 'type'], 'preview'],
      paint: { 'line-color': '#667eea', 'line-width': 2, 'line-dasharray': [4, 4] }
    });

    map.addLayer({
      id: LINE_LAYER, type: 'line', source: SOURCE_ID,
      filter: ['==', ['get', 'type'], 'line'],
      paint: { 'line-color': '#667eea', 'line-width': 3 }
    });

    map.addLayer({
      id: FILL_LAYER, type: 'fill', source: SOURCE_ID,
      filter: ['==', ['get', 'type'], 'fill'],
      paint: { 'fill-color': 'rgba(102, 126, 234, 0.25)' }
    });

    map.addLayer({
      id: POINTS_LAYER, type: 'circle', source: SOURCE_ID,
      filter: ['==', ['get', 'type'], 'points'],
      paint: { 'circle-radius': 5, 'circle-color': '#ffffff', 'circle-stroke-color': '#667eea', 'circle-stroke-width': 2 }
    });
  }

  function updateSource(map) {
    const turf = getTurf();
    const fc = emptyFC();

    if (points.length > 1) {
      fc.features.push({
        type: 'Feature', properties: { type: 'line' },
        geometry: { type: 'LineString', coordinates: points }
      });
    }

    if (activeTool === 'area' && points.length >= 3) {
      fc.features.push({
        type: 'Feature', properties: { type: 'fill' },
        geometry: { type: 'Polygon', coordinates: [points.concat([points[0]])] }
      });
    }

    if (points.length && cursorLngLat && activeTool !== 'search') {
      const previewCoords = activeTool === 'area' && points.length >= 2
        ? points.concat([cursorLngLat, points[0]])
        : points.concat([cursorLngLat]);
      fc.features.push({
        type: 'Feature', properties: { type: 'preview' },
        geometry: { type: 'LineString', coordinates: previewCoords }
      });
    }

    if (points.length) {
      fc.features.push({
        type: 'Feature', properties: { type: 'points' },
        geometry: { type: 'MultiPoint', coordinates: points }
      });
    }

    const src = map.getSource(SOURCE_ID);
    if (src) src.setData(fc);
  }

  function getResult() {
    const turf = getTurf();
    if (!turf || points.length < 2) return null;
    if (activeTool === 'length') {
      const km = turf.length(turf.lineString(points), { units: 'kilometers' });
      return { text: formatDistance(km), km: km };
    }
    if (activeTool === 'area' && points.length >= 3) {
      const m2 = turf.area(turf.polygon([points.concat([points[0]])]));
      return { text: formatArea(m2), m2: m2 };
    }
    return null;
  }

  function popupAnchor() {
    const turf = getTurf();
    if (activeTool === 'area' && points.length >= 3 && turf) {
      return turf.centroid(turf.polygon([points.concat([points[0]])])).geometry.coordinates;
    }
    if (points.length) return points[points.length - 1];
    return cursorLngLat;
  }

  function showPopup() {
    const map = getMap();
    if (!map) return;
    const result = getResult();
    const anchor = popupAnchor();
    if (!anchor) return;
    if (!popup) {
      popup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 8, className: 'measure-tooltip' })
        .setLngLat(anchor).addTo(map);
    } else {
      popup.setLngLat(anchor);
    }
    popup.setHTML(result ? result.text : 'Кликните для первой точки');
  }

  function removePopup() {
    if (popup) { popup.remove(); popup = null; }
  }

  function updateCoordsPanel() {
    const panel = document.getElementById('coords-panel');
    const list = document.getElementById('coords-list');
    if (!panel || !list) return;

    if (activeTool !== 'area' || points.length < 3) {
      panel.classList.remove('visible');
      return;
    }

    panel.classList.add('visible');
    let html = '<div style="margin-bottom:8px;color:#888;font-size:11px">Точек: ' + points.length + '</div>';
    points.forEach(function(coord, i) {
      html += '<div class="coord-item"><strong>' + (i + 1) + ':</strong> ' + coord[1].toFixed(6) + ', ' + coord[0].toFixed(6) + '</div>';
    });
    list.innerHTML = html;
  }

  function clearCoordsPanel() {
    const panel = document.getElementById('coords-panel');
    const list = document.getElementById('coords-list');
    if (panel) panel.classList.remove('visible');
    if (list) list.innerHTML = '';
  }

  function clearGraphics() {
    const map = getMap();
    if (!map) return;
    const src = map.getSource(SOURCE_ID);
    if (src) src.setData(emptyFC());
  }

  function cleanupMeasure() {
    const map = getMap();
    if (!map) return;
    if (measureClickHandler) { map.off('click', measureClickHandler); measureClickHandler = null; }
    if (measureDblClickHandler) { map.off('dblclick', measureDblClickHandler); measureDblClickHandler = null; }
    if (measureMoveHandler) { map.off('mousemove', measureMoveHandler); measureMoveHandler = null; }
    if (keyHandler) { document.removeEventListener('keydown', keyHandler); keyHandler = null; }
    try { map.doubleClickZoom.enable(); } catch (e) {}
    window._mapToolsMeasureMode = false;
    map.getCanvas().style.cursor = '';
  }

  function stopMeasure() {
    cleanupMeasure();
    removePopup();
    clearGraphics();
    clearCoordsPanel();
    points = [];
    cursorLngLat = null;
    activeTool = null;
    updateButtonState();
  }

  function updateButtonState() {
    const menu = document.getElementById('tools-secondary-menu');
    if (!menu) return;
    menu.querySelectorAll('.menu-item-secondary').forEach(function(btn) {
      const isActive = (btn.dataset.tool === activeTool) || (btn.dataset.action === 'search-coords' && activeTool === 'search');
      btn.classList.toggle('active', isActive);
    });
  }

  function addPoint(lngLat) {
    points.push([lngLat.lng, lngLat.lat]);
    const map = getMap();
    if (!map) return;
    updateSource(map);
    showPopup();
    updateCoordsPanel();
  }

  function finishMeasure() {
    const map = getMap();
    if (!map) return;
    if (points.length < 2) {
      stopMeasure();
      return;
    }
    showPopup();
    updateCoordsPanel();
    cleanupMeasure();
    activeTool = null;
    updateButtonState();
  }

  function startMeasure(tool) {
    const map = getMap();
    if (!map) { alert('Карта ещё загружается'); return; }

    stopSearchMode();
    stopMeasure();
    activeTool = tool;
    window._mapToolsMeasureMode = true;
    updateButtonState();

    function bind() {
      ensureSource(map);
      map.getCanvas().style.cursor = 'crosshair';
      try { map.doubleClickZoom.disable(); } catch (e) {}

      measureClickHandler = function(e) {
        e.preventDefault();
        e.originalEvent && e.originalEvent.stopPropagation();
        addPoint(e.lngLat);
      };

      measureMoveHandler = function(e) {
        cursorLngLat = [e.lngLat.lng, e.lngLat.lat];
        updateSource(map);
        if (points.length) showPopup();
      };

      measureDblClickHandler = function(e) {
        e.preventDefault();
        e.originalEvent && e.originalEvent.stopPropagation();
        finishMeasure();
      };

      keyHandler = function(e) {
        if (e.key === 'Escape') stopMeasure();
      };

      map.on('click', measureClickHandler);
      map.on('mousemove', measureMoveHandler);
      map.on('dblclick', measureDblClickHandler);
      document.addEventListener('keydown', keyHandler);
    }

    if (map.isStyleLoaded()) bind();
    else map.once('load', bind);
  }

  /* Coordinate search mode (crosshair) */
  let searchMarker = null;

  function updateCenterCoords() {
    const map = getMap();
    const mousePosEl = document.getElementById('mouse-position');
    if (!map || !mousePosEl || activeTool !== 'search') return;
    const c = map.getCenter();
    mousePosEl.textContent = c.lat.toFixed(6) + ', ' + c.lng.toFixed(6);
  }

  function cleanupSearch() {
    const map = getMap();
    if (!map) return;
    if (searchClickHandler) { map.off('click', searchClickHandler); searchClickHandler = null; }
    if (moveEndHandler) { map.off('moveend', moveEndHandler); moveEndHandler = null; }
    if (keyHandler) { document.removeEventListener('keydown', keyHandler); keyHandler = null; }
  }

  function setSearchMode(active) {
    const map = getMap();
    const centerMarker = document.getElementById('center-marker');
    const mousePosEl = document.getElementById('mouse-position');
    if (!map) return;

    if (!active) {
      stopSearchMode();
      return;
    }

    stopMeasure();
    activeTool = 'search';
    window._mapToolsSearchMode = true;
    updateButtonState();

    if (centerMarker) centerMarker.classList.remove('hidden');
    if (mousePosEl) mousePosEl.style.background = 'rgba(102,126,234,0.9)';
    updateCenterCoords();

    moveEndHandler = function() { updateCenterCoords(); };
    map.on('moveend', moveEndHandler);

    searchClickHandler = function(e) {
      e.preventDefault();
      e.originalEvent && e.originalEvent.stopPropagation();
      const c = map.getCenter();
      const text = c.lat.toFixed(6) + ', ' + c.lng.toFixed(6);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(function() {});
      }
      if (mousePosEl) {
        const original = mousePosEl.textContent;
        mousePosEl.textContent = 'Скопировано';
        setTimeout(function() { mousePosEl.textContent = original; }, 1200);
      }
    };

    keyHandler = function(e) {
      if (e.key === 'Escape') stopSearchMode();
    };

    map.on('click', searchClickHandler);
    document.addEventListener('keydown', keyHandler);
  }

  function stopSearchMode() {
    const centerMarker = document.getElementById('center-marker');
    const mousePosEl = document.getElementById('mouse-position');

    cleanupSearch();

    if (centerMarker) centerMarker.classList.add('hidden');
    if (mousePosEl) {
      mousePosEl.style.background = '';
      mousePosEl.textContent = '55.755800, 37.617300';
    }

    window._mapToolsSearchMode = false;
    activeTool = null;
    updateButtonState();
  }

  /* Mouse position input for manual coordinate entry */
  function initMousePositionInput() {
    const mousePosEl = document.getElementById('mouse-position');
    if (!mousePosEl) return;

    mousePosEl.addEventListener('click', function() {
      if (activeTool !== 'search') return;
      const current = mousePosEl.textContent;
      const input = document.createElement('input');
      input.type = 'text';
      input.value = current;
      input.style.cssText = 'background:transparent;color:#fff;border:none;font-family:monospace;font-size:12px;width:220px;outline:none;';
      mousePosEl.innerHTML = '';
      mousePosEl.appendChild(input);
      input.focus();
      input.select();

      function apply() {
        const val = input.value.trim().replace(/,/g, '.').replace(/\s+/g, ' ');
        const parts = val.split(/[\s,]+/).map(parseFloat).filter(function(n) { return !isNaN(n); });
        mousePosEl.removeChild(input);
        if (parts.length >= 2) {
          let lat, lon;
          if (Math.abs(parts[0]) > 90 && Math.abs(parts[1]) <= 90) { lon = parts[0]; lat = parts[1]; }
          else if (Math.abs(parts[1]) > 90 && Math.abs(parts[0]) <= 90) { lon = parts[1]; lat = parts[0]; }
          else { lat = parts[0]; lon = parts[1]; }
          const map = getMap();
          if (map) {
            map.flyTo({ center: [lon, lat], zoom: 16, duration: 800 });
            if (!searchMarker) searchMarker = new maplibregl.Marker({ color: '#ff0000' }).setLngLat([lon, lat]).addTo(map);
            else searchMarker.setLngLat([lon, lat]);
          }
        }
        updateCenterCoords();
      }

      input.addEventListener('keydown', function(e) { if (e.key === 'Enter') apply(); if (e.key === 'Escape') { mousePosEl.removeChild(input); updateCenterCoords(); } });
      input.addEventListener('blur', apply);
    });
  }

  function initTools() {
    const menu = document.getElementById('tools-secondary-menu');
    console.log('[map-tools] initTools', { menu: !!menu, buttons: menu ? menu.querySelectorAll('.menu-item-secondary').length : 0 });
    if (!menu) return;

    menu.querySelectorAll('.menu-item-secondary').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        console.log('[map-tools] click', this.dataset);

        const tool = this.dataset.tool;
        const action = this.dataset.action;

        if (action === 'search-coords') {
          if (activeTool === 'search') setSearchMode(false);
          else setSearchMode(true);
          return;
        }

        if (!tool) return;
        if (['length', 'area'].indexOf(tool) === -1) return;

        if (activeTool === tool) {
          stopMeasure();
        } else {
          startMeasure(tool);
        }
      });
    });

    initMousePositionInput();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTools);
  } else {
    initTools();
  }
})();
